/**
 * useVersionCheck - Hook to detect app updates
 * 
 * Periodically checks the backend version and compares with the local version.
 * When a new version is detected, triggers an update notification.
 */
import { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOCAL_VERSION = process.env.REACT_APP_VERSION || 'dev';
const CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes
const VERSION_STORAGE_KEY = 'viajejunto_last_version';

export function useVersionCheck() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [newVersion, setNewVersion] = useState(null);
  const [checking, setChecking] = useState(false);

  const checkVersion = useCallback(async () => {
    try {
      setChecking(true);
      const response = await fetch(`${API_URL}/api/version`, {
        cache: 'no-store', // Always bypass cache for version check
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      if (!response.ok) return;
      
      const data = await response.json();
      const serverVersion = data.frontend_version || data.version;
      
      // Get the version we last acknowledged
      const lastAcknowledgedVersion = localStorage.getItem(VERSION_STORAGE_KEY);
      
      // Compare versions
      // Update available if:
      // 1. Server version is different from local build version
      // 2. AND we haven't already acknowledged this version
      if (serverVersion && serverVersion !== LOCAL_VERSION && serverVersion !== lastAcknowledgedVersion) {
        console.log(`[Version Check] New version available: ${serverVersion} (current: ${LOCAL_VERSION})`);
        setUpdateAvailable(true);
        setNewVersion(serverVersion);
      }
    } catch (error) {
      console.warn('[Version Check] Failed to check version:', error.message);
    } finally {
      setChecking(false);
    }
  }, []);

  // Force refresh the app
  const forceUpdate = useCallback(() => {
    // Clear service worker caches
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => {
          if (name.startsWith('viajejunto-')) {
            caches.delete(name);
          }
        });
      });
    }

    // Unregister service worker to force fresh install
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(registration => {
          registration.unregister();
        });
      });
    }

    // Store that we're updating to prevent loop
    if (newVersion) {
      localStorage.setItem(VERSION_STORAGE_KEY, newVersion);
    }

    // Hard refresh bypassing cache
    window.location.reload(true);
  }, [newVersion]);

  // Dismiss the update notification (don't show again for this version)
  const dismissUpdate = useCallback(() => {
    if (newVersion) {
      localStorage.setItem(VERSION_STORAGE_KEY, newVersion);
    }
    setUpdateAvailable(false);
    setNewVersion(null);
  }, [newVersion]);

  // Check on mount and periodically
  useEffect(() => {
    // Initial check after a short delay (let app load first)
    const initialTimeout = setTimeout(checkVersion, 3000);
    
    // Periodic checks
    const interval = setInterval(checkVersion, CHECK_INTERVAL);
    
    // Also check when tab becomes visible (user returns to app)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkVersion();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkVersion]);

  return {
    updateAvailable,
    newVersion,
    currentVersion: LOCAL_VERSION,
    checking,
    forceUpdate,
    dismissUpdate,
    checkNow: checkVersion
  };
}
